import * as React from "react";
import styled from "styled-components";

import Modal, { ModalBody, Sheets } from "@ingka/modal";

import { ManageUserModalButtonWrapper } from "../../styles";
import ListView, { ListViewItem } from "@ingka/list-view";
import { ApplicationUserData } from "../../../services/users";

import { Application } from "../../../store";
import { StyledCommercialMessage, StyledModalHeader } from "../../styles";

import InlineMessage from "@ingka/inline-message";
import { sortMarketUsers } from "../utils";
import { space100 } from "@ingka/variables/design-tokens";

interface ModalLayoutProps {
	activeMarket: string | null;
	getCountryName: (country: string) => string;
	showModal: boolean;
	setShowModal: (value: boolean) => void;
	marketUsersList: ApplicationUserData | undefined;
	activeApplication: Application | undefined;
}

const StyledModalBody = styled(ModalBody)`
	padding: 1rem 3rem;
	.switch {
		padding-top: 0.5rem;
	}
	.inline-message {
		margin: 1rem 0;
	}
`;

const StyledMessage = styled(StyledCommercialMessage)`
	margin-left: 0.5rem;
`;

const StyledTitleMessage = styled(StyledCommercialMessage)`
	margin-top: 1rem;
`;

const StyledListView = styled(ListView)`
	padding: 0rem;
`;

const ListItemWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
	border-bottom: 1px rgb(var(--colour-neutral-3, 223, 223, 223)) solid;
`;

const StyledAvatarListViewItem = styled(ListViewItem)`
	.avatar {
		width: 2.5rem;
		height: 2.5rem;
		flex-shrink: 0;
	}
`;

const UsersListWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${space100};
`;

const Layout: React.FC<ModalLayoutProps> = ({
	activeMarket,
	getCountryName,
	showModal,
	setShowModal,
	marketUsersList,
}) => {
	const { onboardedUsers = [] } = marketUsersList ?? {};

	const allAdmins = React.useMemo(() => onboardedUsers.filter((user) => user["isApplicationAdmin"]), [onboardedUsers]);

	const allMaintainers = React.useMemo(
		() => onboardedUsers.filter((user) => !user["isApplicationAdmin"]),
		[onboardedUsers]
	);

	const hasAnyUser = onboardedUsers.length > 0;
	const hasAdmins = allAdmins.length > 0;
	const hasMaintainers = allMaintainers.length > 0;

	return (
		<>
			<form
				onSubmit={(e) => {
					e.preventDefault();
				}}
			>
				<Modal visible={showModal} handleCloseBtn={() => setShowModal(false)}>
					<Sheets
						aria-labelledby="userInfoModaltitleId"
						footer={<ManageUserModalButtonWrapper></ManageUserModalButtonWrapper>}
						header={
							<StyledModalHeader
								title={`Users assigned to ${activeMarket && getCountryName(activeMarket)}`}
								titleId="userInfoModaltitleId"
							/>
						}
						size="small"
					>
						<StyledModalBody>
							{!hasAnyUser && (
								<InlineMessage body="No users were assigned to this market" variant="cautionary" subtle />
							)}

							<UsersListWrapper>
								{hasAdmins && (
									<div>
										<h3>Admins</h3>
										<StyledListView id="admins" title="admins" showDivider>
											{allAdmins.sort(sortMarketUsers).map((user) => (
												<StyledAvatarListViewItem
													id={user.id}
													key={user.id}
													inset={false}
													title={user.name}
													description={user.email}
													emphasised
													avatarProps={{
														screenReaderText: user.name,
														text: user.name.split("(")[0].replace(/[^A-Z]/g, ""),
													}}
													controlIcon="chevronRightSmall"
												/>
											))}
										</StyledListView>
									</div>
								)}

								{hasMaintainers && (
									<div>
										<h3>Maintainers</h3>
										<StyledListView id="onboarded" title="marinatiners">
											{allMaintainers.sort(sortMarketUsers).map((user) => (
												<ListItemWrapper>
													<StyledAvatarListViewItem
														id={user.id}
														key={user.id}
														inset={false}
														title={user.name}
														description={user.email}
														emphasised
														image={undefined}
														controlIcon="chevronRightSmall"
														avatarProps={{
															screenReaderText: user.name,
															text: user.name.split("(")[0].replace(/[^A-Z]/g, ""),
														}}
													/>
												</ListItemWrapper>
											))}
										</StyledListView>
									</div>
								)}
							</UsersListWrapper>
						</StyledModalBody>
					</Sheets>
				</Modal>
			</form>
		</>
	);
};

export default Layout;
